import React, {FC, useState, useEffect} from "react"
import {withRouter} from "react-router-dom"
import {isValidPhoneNumber, parsePhoneNumber} from "react-phone-number-input"

import {Body, H3} from "@irati/ui/src/Typography"
import FormattedMessage, {useIntl} from "@irati/utils/lang"
import withBranding, {WithBranding} from "@irati/brand/withBranding"
import Flex from "@irati/ui/src/Flex"
import {PhoneInput, ValidationButton} from "@irati/ui/src/FormItem"
import Alert, {AlertTypes} from "@irati/ui/src/Alert"
import Form from "@irati/ui/src/Form"
import Validation from "@irati/utils/validation"
import {OverlayPlacement, Tooltip} from "@irati/ui/src/Tooltip"
import {IconTypes} from "@irati/ui/src/Icon"
import getTenantFromPath from "@irati/identity/helpers/getTenantFromPath"
import {perkboxLogo} from "@irati/identity/helpers/defaultTenant"
import tracking, {LoginProduct, LoginType} from "@irati-tracking/platform/platform"

import {BusinessLogo, StyledIcon} from "./styled"

interface Props extends WithBranding {
  load: Promise<any> | null
  error: string
  onClick: (phone: string, isPhone: boolean) => void
}

const EnterPhone: FC<Props> = ({error, load, branding, onClick}) => {
  const [phone, setPhone] = useState(localStorage.getItem("userPhone") || "")
  const [phoneError, setPhoneError] = useState<string | null>(null)

  const {formatMessage} = useIntl()
  const isTenant = getTenantFromPath()

  const handleOnInputChange = (value) => {
    setPhoneError("")
    setPhone(value)
    localStorage.setItem("userPhone", value || "")
  }

  const handleSubmit = () => {
    if (phone && isValidPhoneNumber(phone)) {
      tracking.loginOTPSubmit("phone_number", undefined, undefined, undefined, phone)
      const parsedPhoneNumber = parsePhoneNumber(phone)?.number || phone
      onClick(parsedPhoneNumber, true)
    }
  }

  useEffect(() => {
    setPhoneError(error)
  }, [error])

  useEffect(() => {
    if (!window.location.pathname.includes("/activate")) {
      localStorage.setItem("login_type", LoginType.phoneOtpLogin)
      localStorage.setItem("login_product", LoginProduct.login)
    }
  }, [])

  return (
    <Flex direction="column" justifyContent="space-between" minHeight={isTenant ? "500px" : "400px"}>
      <Flex direction="column" alignItems="center">
        {!!phoneError && (
          <Alert
            type={AlertTypes.error}
            title={phoneError}
            subTitle="platform.oneTimePassword.enterEmail.error.text"
            onHide={() => setPhoneError(null)}
          />
        )}
        {isTenant && branding?.businessLogoUrl !== perkboxLogo && <BusinessLogo src={formatMessage({id: branding?.businessLogoUrl})} />}
        <H3 marginBottom={2}>
          <FormattedMessage id="platform.oneTimePassword.enterEmail.title" />
        </H3>
        <Flex direction="column" width="100%">
          <Form>
            <PhoneInput
              value={phone}
              validation={Validation.string(formatMessage({id: "platform.mfa.phoneNumber.errors.isRequired"}))}
              label="Enter phone number"
              placeholder="Phone number"
              onChange={handleOnInputChange}
            />
            <ValidationButton load={load} mobileFull full primary onClick={handleSubmit}>
              <FormattedMessage id="platform.oneTimePassword.enterEmail.mobileBtn2" />
            </ValidationButton>

            <Body small marginTop>
              <FormattedMessage id="platform.oneTimePassword.enterEmail.phoneBody" />
              <Tooltip
                tooltipChildren={
                  <Body small padding={[1, 1]}>
                    <FormattedMessage id="platform.oneTimePassword.enterEmail.phoneTooltip" />
                  </Body>
                }
                placement={OverlayPlacement.Bottom}>
                <StyledIcon marginLeft icon={IconTypes.question} size={24} />
              </Tooltip>
            </Body>
          </Form>
        </Flex>
      </Flex>

      <Alert
        marginBottom="0px"
        nonDismissable
        type={AlertTypes.warning}
        title="platform.oneTimePassword.enterEmail.phoneAlertTitle"
        tooltip="platform.oneTimePassword.enterEmail.phoneAlertTooltip"
      />
    </Flex>
  )
}

export default withRouter(withBranding(EnterPhone))
